// Manipulação de texto da barra de formatação da mensagem de aviso
// (views/Config/AvisosConsumidorView.tsx). Fica aqui, fora do componente, porque é
// lógica de índice de seleção — o tipo de coisa que erra por um caractere e só
// aparece como "o cursor pulou" ou "comeu uma letra" na mão do usuário.
//
// Ninguém aqui toca no DOM: recebe texto + posições da seleção, devolve o texto novo
// e onde a seleção deve ficar. Quem chama aplica no textarea.

export interface ResultadoFormatacao {
    texto: string;
    /** Onde reposicionar a seleção no textarea depois de aplicar. */
    selecaoInicio: number;
    selecaoFim: number;
}

/**
 * Envolve o trecho selecionado com a marca do markdown (`**` negrito, `*` itálico).
 * Sem nada selecionado, insere `exemplo` já marcado e o deixa selecionado, para o
 * usuário digitar por cima.
 *
 * Já marcado, desmarca — o mesmo botão liga e desliga, como em qualquer editor.
 */
export function envolverSelecao(
    texto: string,
    inicio: number,
    fim: number,
    marca: string,
    exemplo: string
): ResultadoFormatacao {
    const selecionado = texto.slice(inicio, fim);

    // Desmarcar tem dois casos: a marca dentro da seleção (**negrito** selecionado
    // inteiro) e a marca em volta dela (negrito selecionado, ** de fora). Sem o
    // segundo caso, clicar em B duas vezes na mesma palavra gera ****palavra****.
    //
    // Nos dois casos é preciso checar o caractere vizinho: `*` e `**` compartilham o
    // mesmo símbolo, então pedir itálico em **total** encontra um `*` de cada lado e,
    // sem essa checagem, "desmarcaria" comendo metade do negrito (vira *total*).
    const simbolo = marca[0];
    const interior = selecionado.slice(marca.length, selecionado.length - marca.length);
    const marcaIsoladaDentro =
        selecionado.length > 2 * marca.length &&
        selecionado.startsWith(marca) &&
        selecionado.endsWith(marca) &&
        !interior.startsWith(simbolo) &&
        !interior.endsWith(simbolo);

    if (marcaIsoladaDentro) {
        return {
            texto: `${texto.slice(0, inicio)}${interior}${texto.slice(fim)}`,
            selecaoInicio: inicio,
            selecaoFim: inicio + interior.length,
        };
    }

    const antes = texto.slice(Math.max(0, inicio - marca.length), inicio);
    const depois = texto.slice(fim, fim + marca.length);
    const vizinhoEsquerdo = inicio - marca.length - 1 >= 0 ? texto[inicio - marca.length - 1] : '';
    const vizinhoDireito = texto[fim + marca.length] ?? '';
    const marcaIsoladaFora =
        antes === marca &&
        depois === marca &&
        vizinhoEsquerdo !== simbolo &&
        vizinhoDireito !== simbolo;

    if (selecionado.length > 0 && marcaIsoladaFora) {
        return {
            texto: `${texto.slice(0, inicio - marca.length)}${selecionado}${texto.slice(fim + marca.length)}`,
            selecaoInicio: inicio - marca.length,
            selecaoFim: inicio - marca.length + selecionado.length,
        };
    }

    const conteudo = selecionado || exemplo;
    return {
        texto: `${texto.slice(0, inicio)}${marca}${conteudo}${marca}${texto.slice(fim)}`,
        selecaoInicio: inicio + marca.length,
        selecaoFim: inicio + marca.length + conteudo.length,
    };
}

/**
 * Liga/desliga lista com marcadores nas linhas tocadas pela seleção (ou na linha do
 * cursor). Diferente do negrito, age por linha inteira: markdown só entende `- ` no
 * começo da linha.
 *
 * Linhas em branco no meio do bloco ficam como estão — prefixar `- ` nelas criaria
 * item vazio na lista publicada.
 */
export function alternarListaSelecao(texto: string, inicio: number, fim: number): ResultadoFormatacao {
    const inicioLinha = texto.lastIndexOf('\n', Math.max(0, inicio - 1)) + 1;
    const fimBruto = texto.indexOf('\n', fim);
    const fimLinha = fimBruto === -1 ? texto.length : fimBruto;

    const bloco = texto.slice(inicioLinha, fimLinha);
    const linhas = (bloco.trim() === '' ? 'item' : bloco).split('\n');

    // "Já é lista" ignora linhas vazias: um bloco com uma linha em branco no meio
    // continua sendo lista, e o segundo clique tem que desmarcá-lo.
    const comConteudo = linhas.filter((l) => l.trim() !== '');
    const jaLista = comConteudo.length > 0 && comConteudo.every((l) => l.trimStart().startsWith('- '));

    const novoBloco = linhas
        .map((l) => {
            if (l.trim() === '') return l;
            return jaLista ? l.replace(/^(\s*)- /, '$1') : `- ${l}`;
        })
        .join('\n');

    return {
        texto: `${texto.slice(0, inicioLinha)}${novoBloco}${texto.slice(fimLinha)}`,
        selecaoInicio: inicioLinha,
        selecaoFim: inicioLinha + novoBloco.length,
    };
}
